/**
 * Fine-Tuning Management Page
 * Manage training jobs, datasets, and model fine-tuning configurations
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Avatar,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FineTuningService } from '../../services/FineTuningService';
import { DeploymentInstanceService } from '../../services/DeploymentInstanceService';
import { TrainingFile } from '../../repositories/FineTuningRepository';
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Stop as StopIcon,
  Folder as FolderIcon,
  ModelTraining as ModelTrainingIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';

export function FineTuningPage() {
  const navigate = useNavigate();
  const fineTuningService = new FineTuningService();
  const deploymentInstanceService = new DeploymentInstanceService();
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [createJobDialogOpen, setCreateJobDialogOpen] = useState(false);
  const [uploadDatasetDialogOpen, setUploadDatasetDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [instances, setInstances] = useState<any[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [trainingFiles, setTrainingFiles] = useState<TrainingFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedInstanceForFiles, setSelectedInstanceForFiles] = useState<string>('');
  const [trainingJobs, setTrainingJobs] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [createModelDialogOpen, setCreateModelDialogOpen] = useState(false);
  const [creatingModel, setCreatingModel] = useState(false);

  // Initial job form state
  const initialJobState = {
    name: '',
    instance_id: '',
    training_data_path: "",
    dataset: '',
    epochs: 2,
    batch_size: 4,
    learning_rate: 0.0002,
    warmup_ratio: 0.03,
    gradient_accumulation_steps: 1,
    weight_decay: 0.01,
    enable_fp16: true,
    save_steps: 0,
    eval_steps: 25,
    target_modules: ["q_proj", "v_proj"],
    optimizer: 'paged_adamw_8bit',
    lora_dropout: 0.1,
    lora_rank: 64,
    lora_alpha: 16,
    bit4_quantization: true,
    bit4_quantization_type: 'nf4',
    bit4_compute_dtype: 'torch.bfloat16',
    fine_tuned_model_path: 'llm-sft-models',
  };

  // New job form state
  const [newJob, setNewJob] = useState(initialJobState);

  // Fetch instances on mount
  useEffect(() => {
    fetchInstances();
    fetchTrainingJobs();
  }, []);

  // Fetch training files when selected instance changes
  useEffect(() => {
    if (selectedInstanceForFiles) {
      fetchTrainingFiles();
    }
  }, [selectedInstanceForFiles]);

  const fetchTrainingJobs = async () => {
    setLoadingJobs(true);
    try {
      const jobs = await fineTuningService.getTrainingJobs('dev');
      console.log('Training jobs:', jobs);
      setTrainingJobs(jobs);
    } catch (error) {
      console.error('Error fetching training jobs:', error);
      toast.error('Failed to load training jobs');
      setTrainingJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchInstances = async () => {
    setLoadingInstances(true);
    try {
      const response = await deploymentInstanceService.getDeploymentInstances();
      console.log('Instances response:', response);
      
      // Handle different response structures
      const instancesData = Array.isArray(response) ? response : (response?.data || response?.instances || []);
      console.log('Instances data:', instancesData);
      
      // Filter for running instances only (case-insensitive check)
      const runningInstances = instancesData.filter((instance: any) => 
        instance.instance_status?.toLowerCase() === 'running'
      );
      console.log('Running instances:', runningInstances);
      
      setInstances(runningInstances);
      
      // Set the first running instance as selected if available
      if (runningInstances.length > 0) {
        const firstInstanceId = String(runningInstances[0].instance_id);
        console.log('Setting first running instance ID:', firstInstanceId);
        setSelectedInstanceForFiles(firstInstanceId);
      } else {
        console.log('No running instances available');
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
      toast.error('Failed to load instances');
      setInstances([]);
    } finally {
      setLoadingInstances(false);
    }
  };

  const fetchTrainingFiles = async () => {
    if (!selectedInstanceForFiles) {
      console.log('No instance selected, skipping fetch');
      return;
    }
    
    console.log('Fetching training files for instance:', selectedInstanceForFiles);
    setLoadingFiles(true);
    try {
      const files = await fineTuningService.listTrainingFiles(selectedInstanceForFiles);
      console.log('Training files:', files);
      setTrainingFiles(files);
    } catch (error) {
      console.error('Error fetching training files:', error);
      toast.error('Failed to load training files');
      setTrainingFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, id: string, job?: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(id);
    if (job) {
      setSelectedJob(job);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
    setSelectedJob(null);
  };

  // Model & Dataset selection validation schema
  const modelDatasetValidationSchema = Yup.object({
    instance_id: Yup.string().required('Instance ID is required'),
    dataset: Yup.string().required('Dataset is required'),
  });

  const modelDatasetFormik = useFormik({
    initialValues: {
      instance_id: '',
      dataset: '',
    },
    validationSchema: modelDatasetValidationSchema,
    onSubmit: (values) => {
      // Update newJob with selected values
      setNewJob((prev) => ({ ...prev, ...values }));
      handleNext();
    },
  });

  // Training configuration validation schema
  const trainingConfigValidationSchema = Yup.object({
    epochs: Yup.number().required('Epochs is required').min(1).max(10),
    batch_size: Yup.number().required('Batch size is required').min(1).max(32),
    learning_rate: Yup.number().required('Learning rate is required').min(0.00001).max(0.01),
    warmup_ratio: Yup.number().required('Warmup ratio is required').min(0).max(1),
    gradient_accumulation_steps: Yup.number().required('Gradient accumulation steps is required').min(1),
    weight_decay: Yup.number().required('Weight decay is required').min(0).max(1),
    enable_fp16: Yup.boolean(),
    save_steps: Yup.number().required('Save steps is required').min(0),
    eval_steps: Yup.number().required('Eval steps is required').min(1),
    optimizer: Yup.string().required('Optimizer is required'),
    lora_dropout: Yup.number().required('LoRA dropout is required').min(0).max(1),
    lora_rank: Yup.number().required('LoRA rank is required').min(1),
    lora_alpha: Yup.number().required('LoRA alpha is required').min(1),
    bit4_quantization: Yup.boolean(),
    bit4_quantization_type: Yup.string(),
    bit4_compute_dtype: Yup.string(),
    fine_tuned_model_path: Yup.string().required('Fine-tuned model path is required'),
  });

  const trainingConfigFormik = useFormik({
    initialValues: {
      epochs: 2,
      batch_size: 4,
      learning_rate: 0.0002,
      warmup_ratio: 0.03,
      gradient_accumulation_steps: 1,
      weight_decay: 0.01,
      enable_fp16: true,
      save_steps: 0,
      eval_steps: 25,
      optimizer: 'paged_adamw_8bit',
      lora_dropout: 0.1,
      lora_rank: 64,
      lora_alpha: 16,
      bit4_quantization: true,
      bit4_quantization_type: 'nf4',
      bit4_compute_dtype: 'torch.bfloat16',
      fine_tuned_model_path: 'llm-sft-models',
    },
    validationSchema: trainingConfigValidationSchema,
    onSubmit: (values) => {
      // Update newJob with training config values
      setNewJob((prev) => ({ ...prev, ...values }));
      handleNext();
    },
  });

  const handleCreateJob = async () => {
    try {
      setCreatingJob(true);
      // Prepare the request payload
      const request = {
        instance_id: newJob.instance_id,
        training_data_path: newJob.dataset, // dataset contains relative_path from training files
        epochs: newJob.epochs,
        batch_size: newJob.batch_size,
        learning_rate: newJob.learning_rate,
        warmup_ratio: newJob.warmup_ratio,
        gradient_accumulation_steps: newJob.gradient_accumulation_steps,
        weight_decay: newJob.weight_decay,
        enable_fp16: newJob.enable_fp16,
        save_steps: newJob.save_steps,
        eval_steps: newJob.eval_steps,
        optimizer: newJob.optimizer,
        lora_dropout: newJob.lora_dropout,
        lora_rank: newJob.lora_rank,
        lora_alpha: newJob.lora_alpha,
        bit4_quantization: newJob.bit4_quantization,
        bit4_quantization_type: newJob.bit4_quantization_type,
        bit4_compute_dtype: newJob.bit4_compute_dtype,
        fine_tuned_model_path: newJob.fine_tuned_model_path,
      };

      // Call the API to start the training job
      const response = await fineTuningService.startTrainingJob(request);
      
      console.log('Training job started:', response);
      toast.success(response.message || 'Training job started successfully');
      
      // Close dialog and reset form
      setCreateJobDialogOpen(false);
      setActiveStep(0);
      setNewJob(initialJobState);
      modelDatasetFormik.resetForm();
      trainingConfigFormik.resetForm();
      
      // Refresh both training jobs and datasets tables
      fetchTrainingJobs();
      fetchTrainingFiles();
    } catch (error: any) {
      console.error('Error starting training job:', error);
      const errorMessage = error.message || 'Failed to start training job';
      toast.error(errorMessage);
    } finally {
      setCreatingJob(false);
    }
  };

  const uploadDatasetValidationSchema = Yup.object({
    instance_id: Yup.string().required('Instance ID is required'),
    file: Yup.mixed()
      .required('File is required')
      .test('fileSize', 'File is too large (max 100MB)', (value) => {
        if (!value) return false;
        return (value as File).size <= 100 * 1024 * 1024; // 100MB
      })
      .test('fileType', 'Unsupported file format', (value) => {
        if (!value) return false;
        const file = value as File;
        return ['application/json', 'text/csv', 'application/x-jsonlines'].includes(file.type) ||
               file.name.endsWith('.jsonl') || file.name.endsWith('.json') || file.name.endsWith('.csv');
      }),
  });

  const uploadDatasetFormik = useFormik({
    initialValues: {
      instance_id: '',
      file: null as File | null,
    },
    validationSchema: uploadDatasetValidationSchema,
    onSubmit: async (values) => {
      if (!values.file || !values.instance_id) return;
      
      try {
        // Call the service to upload dataset
        const response = await fineTuningService.uploadDataset(values.file, values.instance_id);
        
        console.log('API Response:', response);
        toast.success(response.message || 'Dataset uploaded and validation started');
        setUploadDatasetDialogOpen(false);
        uploadDatasetFormik.resetForm();
        
        // Refresh the datasets table to show the newly uploaded file
        fetchTrainingFiles();
      } catch (error: any) {
        console.error('Error uploading dataset:', error);
        const errorMessage = error.message || 'Failed to upload dataset';
        toast.error(errorMessage);
      }
    },
  });

  const handleFileSelect = (file: File) => {
    uploadDatasetFormik.setFieldValue('file', file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleStopJob = () => {
    toast.info('Training job stopped');
    handleMenuClose();
  };

  const handleDeleteJob = () => {
    toast.success('Training job deleted');
    handleMenuClose();
  };

  const handleCreateModelClick = () => {
    // Open confirmation dialog
    setCreateModelDialogOpen(true);
    // Close menu but keep selectedJob for the confirmation dialog
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleCreateModelConfirm = async () => {
    console.log('handleCreateModelConfirm called');
    console.log('selectedJob:', selectedJob);
    
    if (selectedJob) {
      try {
        setCreatingModel(true);
        console.log('Creating model, setting loader...');
        
        // Parse the job name to extract instance_id and model name
        // Format: ddd3b4d05b5f-gemma-2-9b-it
        // Before first '-' is instance_id, remaining is model name
        const jobName = selectedJob.name;
        const firstDashIndex = jobName.indexOf('-');
        
        if (firstDashIndex === -1) {
          toast.error('Invalid job name format');
          setCreateModelDialogOpen(false);
          setCreatingModel(false);
          return;
        }
        
        const instanceIdPart = jobName.substring(0, firstDashIndex);
        const modelNamePart = jobName.substring(firstDashIndex + 1);
        
        // Prepare the payload
        const payload = {
          instance_id: `inst-${instanceIdPart}`,
          fine_tuned_model_path: `models/${modelNamePart}-ft`,
          file_share_path: 'llm-sft-models'
        };
        
        console.log('Calling API with payload:', payload);
        
        // Call the API
        const response = await fineTuningService.createModelConfig(payload);
        
        console.log('API response:', response);
        toast.success(response.message || 'Model configuration created successfully');
        
        // Close dialog and refresh the training jobs list
        setCreateModelDialogOpen(false);
        fetchTrainingJobs();
      } catch (error: any) {
        console.error('Error creating model:', error);
        const errorMessage = error.message || 'Failed to create model configuration';
        toast.error(errorMessage);
        setCreateModelDialogOpen(false);
      } finally {
        setCreatingModel(false);
      }
    } else {
      console.log('No selectedJob found!');
    }
  };

  const handleCreateModelCancel = () => {
    setCreateModelDialogOpen(false);
    setSelectedJob(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'passed':
        return 'success';
      case 'running':
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'queued':
        return 'info';
      default:
        return 'default';
    }
  };

  const steps = ['Select Model & Dataset', 'Configure Training', 'Review & Start'];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleOpenCreateJobDialog = () => {
    // Reset form to initial state
    setNewJob(initialJobState);
    // Reset to first step
    setActiveStep(0);
    // Reset formik forms
    modelDatasetFormik.resetForm();
    trainingConfigFormik.resetForm();
    // Open dialog
    setCreateJobDialogOpen(true);
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                Fine-Tuning
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b' }}>
                Train and optimize models with your custom datasets
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper sx={{ borderRadius: 3, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              px: 2,
              borderBottom: '1px solid #e2e8f0',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
              }
            }}
          >
            <Tab label="Datasets" />
            <Tab label="Training Jobs" />
          </Tabs>

          {/* Datasets Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Training Files {loadingFiles && <CircularProgress size={20} sx={{ ml: 2 }} />}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setUploadDatasetDialogOpen(true)}
                  sx={{ borderRadius: 2 }}
                >
                  Upload Dataset
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Filename</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Relative Path</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Size (MB)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Line Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingFiles ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={40} />
                          <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
                            Loading training files...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : trainingFiles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            No training files found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      trainingFiles.map((file, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ bgcolor: '#f1f5f9', width: 32, height: 32 }}>
                                <FolderIcon sx={{ color: '#64748b', fontSize: 18 }} />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {file.filename}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                              {file.relative_path}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={`${file.size_mb.toFixed(2)} MB`} size="small" sx={{ bgcolor: '#f1f5f9' }} />
                          </TableCell>
                          <TableCell>{file.line_count.toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Training Jobs Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Training Jobs {loadingJobs && <CircularProgress size={20} sx={{ ml: 2 }} />}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateJobDialog}
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: '#2563eb',
                    '&:hover': { bgcolor: '#1d4ed8' }
                  }}
                >
                  New Training Job
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Job Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Namespace</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Succeeded</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Failed</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingJobs ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={40} />
                          <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
                            Loading training jobs...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : trainingJobs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            No training jobs found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      trainingJobs.map((job) => (
                        <TableRow key={job.name} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ bgcolor: '#f1f5f9', width: 32, height: 32 }}>
                                <ModelTrainingIcon sx={{ color: '#64748b', fontSize: 18 }} />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {job.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={job.namespace} size="small" sx={{ bgcolor: '#f1f5f9' }} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={job.status}
                              size="small"
                              color={getStatusColor(job.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={job.active} size="small" color={job.active > 0 ? 'warning' : 'default'} />
                          </TableCell>
                          <TableCell>
                            <Chip label={job.succeeded} size="small" color={job.succeeded > 0 ? 'success' : 'default'} />
                          </TableCell>
                          <TableCell>
                            <Chip label={job.failed} size="small" color={job.failed > 0 ? 'error' : 'default'} />
                          </TableCell>
                          <TableCell>{job.duration || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              {new Date(job.creation_time).toLocaleDateString()} {new Date(job.creation_time).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={(e) => handleMenuClick(e, job.name, job)}>
                              <MoreVertIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {activeTab === 1 && selectedJob?.status?.toLowerCase() === 'completed' && (
          <MenuItem onClick={handleCreateModelClick}>
            <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> Create Model
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteJob} sx={{ color: '#dc2626' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Create Training Job Dialog */}
      <Dialog
        open={createJobDialogOpen}
        onClose={() => setCreateJobDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Create Training Job</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <form onSubmit={modelDatasetFormik.handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControl 
                  fullWidth
                  error={modelDatasetFormik.touched.instance_id && Boolean(modelDatasetFormik.errors.instance_id)}
                >
                  <InputLabel id="instance-select-label">Instance ID</InputLabel>
                  <Select
                    labelId="instance-select-label"
                    id="instance-select"
                    name="instance_id"
                    value={modelDatasetFormik.values.instance_id}
                    label="Instance ID"
                    onChange={(e) => {
                      const selectedValue = String(e.target.value);
                      modelDatasetFormik.setFieldValue('instance_id', selectedValue);
                      modelDatasetFormik.setFieldValue('dataset', '');
                      setNewJob((prev) => ({
                        ...prev,
                        instance_id: selectedValue,
                        dataset: ''
                      }));
                    }}
                    onBlur={modelDatasetFormik.handleBlur}
                    disabled={loadingInstances}
                  >
                    {loadingInstances ? (
                      <MenuItem disabled value="">
                        <CircularProgress size={20} sx={{ mr: 1 }} /> Loading instances...
                      </MenuItem>
                    ) : instances.length === 0 ? (
                      <MenuItem disabled value="">No instances available</MenuItem>
                    ) : (
                      instances.map((instance) => {
                        const instanceId = String(instance.instance_id);
                        return (
                        <MenuItem key={instanceId} value={instanceId}>
                          {instanceId} - {instance.model_name || instance.name || 'Unnamed Instance'}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
                {modelDatasetFormik.touched.instance_id && modelDatasetFormik.errors.instance_id && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.75 }}>
                    {modelDatasetFormik.errors.instance_id}
                  </Typography>
                )}
              </FormControl>
              <FormControl 
                fullWidth
                error={modelDatasetFormik.touched.dataset && Boolean(modelDatasetFormik.errors.dataset)}
              >
                <InputLabel>Dataset</InputLabel>
                <Select
                  name="dataset"
                  value={modelDatasetFormik.values.dataset}
                  label="Dataset"
                  onChange={(e) => {
                    modelDatasetFormik.setFieldValue('dataset', e.target.value);
                    setNewJob((prev) => ({ ...prev, dataset: e.target.value }));
                  }}
                  onBlur={modelDatasetFormik.handleBlur}
                  disabled={loadingFiles}
                >
                  {loadingFiles ? (
                    <MenuItem disabled value="">
                      <CircularProgress size={20} sx={{ mr: 1 }} /> Loading datasets...
                    </MenuItem>
                  ) : trainingFiles.length === 0 ? (
                    <MenuItem disabled value="">No datasets available</MenuItem>
                  ) : (
                    trainingFiles.map((file, index) => (
                      <MenuItem key={index} value={file.relative_path}>
                        {file.filename} ({file.size_mb ? `${file.size_mb.toFixed(2)} MB` : ''}{file.size_mb && file.line_count ? ', ' : ''}{file.line_count ? `${file.line_count.toLocaleString()} lines` : ''})
                      </MenuItem>
                    ))
                  )}
                </Select>
                {modelDatasetFormik.touched.dataset && modelDatasetFormik.errors.dataset && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.75 }}>
                    {modelDatasetFormik.errors.dataset}
                  </Typography>
                )}
              </FormControl>
            </Box>
            </form>
          )}

          {activeStep === 1 && (
            <form onSubmit={trainingConfigFormik.handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: '60vh', overflowY: 'auto', pr: 2 }}>
                {/* Basic Training Parameters */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  Basic Training Parameters
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                        Epochs: {trainingConfigFormik.values.epochs}
                      </Typography>
                      <Slider
                        name="epochs"
                        value={trainingConfigFormik.values.epochs}
                        onChange={(_, value) => trainingConfigFormik.setFieldValue('epochs', value)}
                        min={1}
                        max={10}
                        marks
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                        Batch Size: {trainingConfigFormik.values.batch_size}
                      </Typography>
                      <Slider
                        name="batch_size"
                        value={trainingConfigFormik.values.batch_size}
                        onChange={(_, value) => trainingConfigFormik.setFieldValue('batch_size', value)}
                        min={1}
                        max={32}
                        marks
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Learning Rate"
                      name="learning_rate"
                      type="number"
                      fullWidth
                      value={trainingConfigFormik.values.learning_rate}
                      onChange={trainingConfigFormik.handleChange}
                      onBlur={trainingConfigFormik.handleBlur}
                      error={trainingConfigFormik.touched.learning_rate && Boolean(trainingConfigFormik.errors.learning_rate)}
                      helperText={trainingConfigFormik.touched.learning_rate && trainingConfigFormik.errors.learning_rate}
                      inputProps={{ step: 0.00001, min: 0.00001, max: 0.01 }}
                    />

                    <TextField
                      label="Warmup Ratio"
                      name="warmup_ratio"
                      type="number"
                      fullWidth
                      value={trainingConfigFormik.values.warmup_ratio}
                      onChange={trainingConfigFormik.handleChange}
                      onBlur={trainingConfigFormik.handleBlur}
                      error={trainingConfigFormik.touched.warmup_ratio && Boolean(trainingConfigFormik.errors.warmup_ratio)}
                      helperText={trainingConfigFormik.touched.warmup_ratio && trainingConfigFormik.errors.warmup_ratio}
                      inputProps={{ step: 0.01, min: 0, max: 1 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Gradient Accumulation Steps"
                      name="gradient_accumulation_steps"
                      type="number"
                      fullWidth
                      value={trainingConfigFormik.values.gradient_accumulation_steps}
                      onChange={trainingConfigFormik.handleChange}
                      onBlur={trainingConfigFormik.handleBlur}
                      error={trainingConfigFormik.touched.gradient_accumulation_steps && Boolean(trainingConfigFormik.errors.gradient_accumulation_steps)}
                      helperText={trainingConfigFormik.touched.gradient_accumulation_steps && trainingConfigFormik.errors.gradient_accumulation_steps}
                      inputProps={{ min: 1 }}
                    />

                    <TextField
                      label="Weight Decay"
                      name="weight_decay"
                      type="number"
                      fullWidth
                      value={trainingConfigFormik.values.weight_decay}
                      onChange={trainingConfigFormik.handleChange}
                      onBlur={trainingConfigFormik.handleBlur}
                      error={trainingConfigFormik.touched.weight_decay && Boolean(trainingConfigFormik.errors.weight_decay)}
                      helperText={trainingConfigFormik.touched.weight_decay && trainingConfigFormik.errors.weight_decay}
                      inputProps={{ step: 0.01, min: 0, max: 1 }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Optimization Settings */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  Optimization Settings
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Save Steps"
                      name="save_steps"
                      type="number"
                      fullWidth
                      value={trainingConfigFormik.values.save_steps}
                      onChange={trainingConfigFormik.handleChange}
                      onBlur={trainingConfigFormik.handleBlur}
                      error={trainingConfigFormik.touched.save_steps && Boolean(trainingConfigFormik.errors.save_steps)}
                      helperText={trainingConfigFormik.touched.save_steps && trainingConfigFormik.errors.save_steps}
                      inputProps={{ min: 0 }}
                    />

                    <TextField
                      label="Eval Steps"
                      name="eval_steps"
                      type="number"
                      fullWidth
                      value={trainingConfigFormik.values.eval_steps}
                      onChange={trainingConfigFormik.handleChange}
                      onBlur={trainingConfigFormik.handleBlur}
                      error={trainingConfigFormik.touched.eval_steps && Boolean(trainingConfigFormik.errors.eval_steps)}
                      helperText={trainingConfigFormik.touched.eval_steps && trainingConfigFormik.errors.eval_steps}
                      inputProps={{ min: 1 }}
                    />
                  </Box>

                  <FormControl fullWidth>
                    <InputLabel>Optimizer</InputLabel>
                    <Select
                      name="optimizer"
                      value={trainingConfigFormik.values.optimizer}
                      label="Optimizer"
                      onChange={trainingConfigFormik.handleChange}
                    >
                      <MenuItem value="paged_adamw_8bit">Paged AdamW 8bit</MenuItem>
                      <MenuItem value="adamw">AdamW</MenuItem>
                      <MenuItem value="sgd">SGD</MenuItem>
                      <MenuItem value="adam">Adam</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        name="enable_fp16"
                        checked={trainingConfigFormik.values.enable_fp16}
                        onChange={trainingConfigFormik.handleChange}
                      />
                    }
                    label="Enable FP16 (Mixed Precision Training)"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* LoRA Configuration */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  LoRA Configuration
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="LoRA Dropout"
                      name="lora_dropout"
                      type="number"
                      fullWidth
                      value={trainingConfigFormik.values.lora_dropout}
                      onChange={trainingConfigFormik.handleChange}
                      onBlur={trainingConfigFormik.handleBlur}
                      error={trainingConfigFormik.touched.lora_dropout && Boolean(trainingConfigFormik.errors.lora_dropout)}
                      helperText={trainingConfigFormik.touched.lora_dropout && trainingConfigFormik.errors.lora_dropout}
                      inputProps={{ step: 0.01, min: 0, max: 1 }}
                    />

                    <TextField
                      label="LoRA Rank"
                      name="lora_rank"
                      type="number"
                      fullWidth
                      value={trainingConfigFormik.values.lora_rank}
                      onChange={trainingConfigFormik.handleChange}
                      onBlur={trainingConfigFormik.handleBlur}
                      error={trainingConfigFormik.touched.lora_rank && Boolean(trainingConfigFormik.errors.lora_rank)}
                      helperText={trainingConfigFormik.touched.lora_rank && trainingConfigFormik.errors.lora_rank}
                      inputProps={{ min: 1 }}
                    />
                  </Box>

                  <TextField
                    label="LoRA Alpha"
                    name="lora_alpha"
                    type="number"
                    fullWidth
                    value={trainingConfigFormik.values.lora_alpha}
                    onChange={trainingConfigFormik.handleChange}
                    onBlur={trainingConfigFormik.handleBlur}
                    error={trainingConfigFormik.touched.lora_alpha && Boolean(trainingConfigFormik.errors.lora_alpha)}
                    helperText={trainingConfigFormik.touched.lora_alpha && trainingConfigFormik.errors.lora_alpha}
                    inputProps={{ min: 1 }}
                    sx={{ maxWidth: '50%' }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Quantization Settings */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  Quantization Settings
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="bit4_quantization"
                        checked={trainingConfigFormik.values.bit4_quantization}
                        onChange={trainingConfigFormik.handleChange}
                      />
                    }
                    label="Enable 4-bit Quantization"
                  />

                  {trainingConfigFormik.values.bit4_quantization && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControl fullWidth>
                        <InputLabel>4-bit Quantization Type</InputLabel>
                        <Select
                          name="bit4_quantization_type"
                          value={trainingConfigFormik.values.bit4_quantization_type}
                          label="4-bit Quantization Type"
                          onChange={trainingConfigFormik.handleChange}
                        >
                          <MenuItem value="nf4">NF4</MenuItem>
                          <MenuItem value="fp4">FP4</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel>4-bit Compute Dtype</InputLabel>
                        <Select
                          name="bit4_compute_dtype"
                          value={trainingConfigFormik.values.bit4_compute_dtype}
                          label="4-bit Compute Dtype"
                          onChange={trainingConfigFormik.handleChange}
                        >
                          <MenuItem value="torch.bfloat16">torch.bfloat16</MenuItem>
                          <MenuItem value="torch.float16">torch.float16</MenuItem>
                          <MenuItem value="torch.float32">torch.float32</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Output Path */}
                <TextField
                  label="Fine-tuned Model Path"
                  name="fine_tuned_model_path"
                  fullWidth
                  value={trainingConfigFormik.values.fine_tuned_model_path}
                  onChange={trainingConfigFormik.handleChange}
                  onBlur={trainingConfigFormik.handleBlur}
                  error={trainingConfigFormik.touched.fine_tuned_model_path && Boolean(trainingConfigFormik.errors.fine_tuned_model_path)}
                  helperText={trainingConfigFormik.touched.fine_tuned_model_path && trainingConfigFormik.errors.fine_tuned_model_path}
                  placeholder="e.g., llm-sft-models"
                />
              </Box>
            </form>
          )}

          {activeStep === 2 && (
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 2 }}>
              {/* Model & Dataset Section */}
              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#0f172a' }}>
                  Model & Dataset
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                      Instance ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: '#0f172a' }}>
                      {newJob.instance_id || 'Not selected'}
                    </Typography>
                    {newJob.instance_id && (
                      <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
                        {instances.find(i => String(i.instance_id) === newJob.instance_id)?.model_name || 
                         instances.find(i => String(i.instance_id) === newJob.instance_id)?.name || 
                         'Model instance'}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                      Dataset
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: '#0f172a' }}>
                      {newJob.dataset ? (
                        trainingFiles.find(f => f.relative_path === newJob.dataset)?.filename || newJob.dataset
                      ) : (
                        'Not selected'
                      )}
                    </Typography>
                    {newJob.dataset && trainingFiles.find(f => f.relative_path === newJob.dataset) && (
                      <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
                        {trainingFiles.find(f => f.relative_path === newJob.dataset)?.size_mb && 
                          `${trainingFiles.find(f => f.relative_path === newJob.dataset)?.size_mb.toFixed(2)} MB`}
                        {trainingFiles.find(f => f.relative_path === newJob.dataset)?.size_mb && 
                         trainingFiles.find(f => f.relative_path === newJob.dataset)?.line_count && ' • '}
                        {trainingFiles.find(f => f.relative_path === newJob.dataset)?.line_count && 
                          `${trainingFiles.find(f => f.relative_path === newJob.dataset)?.line_count.toLocaleString()} lines`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#0f172a' }}>
                  Basic Training Parameters
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Epochs</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.epochs}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Batch Size</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.batch_size}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Learning Rate</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.learning_rate}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Warmup Ratio</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.warmup_ratio}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Gradient Accumulation Steps</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.gradient_accumulation_steps}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Weight Decay</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.weight_decay}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#0f172a' }}>
                  Optimization Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Optimizer</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.optimizer}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>FP16 Mixed Precision</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.enable_fp16 ? 'Enabled' : 'Disabled'}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Save Steps</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.save_steps}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Eval Steps</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.eval_steps}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#0f172a' }}>
                  LoRA Configuration
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>LoRA Dropout</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.lora_dropout}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>LoRA Rank</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.lora_rank}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>LoRA Alpha</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.lora_alpha}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#0f172a' }}>
                  Quantization Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>4-bit Quantization</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.bit4_quantization ? 'Enabled' : 'Disabled'}</Typography>
                  </Box>
                  {newJob.bit4_quantization && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Quantization Type</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.bit4_quantization_type.toUpperCase()}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Compute Dtype</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.bit4_compute_dtype}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#0f172a' }}>
                  Output
                </Typography>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem' }}>Fine-tuned Model Path</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{newJob.fine_tuned_model_path}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateJobDialogOpen(false)} disabled={creatingJob}>
            Cancel
          </Button>
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={creatingJob}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button 
              variant="contained" 
              onClick={
                activeStep === 0 
                  ? () => modelDatasetFormik.handleSubmit()
                  : activeStep === 1 
                  ? () => trainingConfigFormik.handleSubmit() 
                  : handleNext
              }
            >
              Next
            </Button>
          ) : (
            <Button 
              variant="contained" 
              onClick={handleCreateJob}
              disabled={creatingJob}
              startIcon={creatingJob ? <CircularProgress size={20} /> : null}
            >
              {creatingJob ? 'Starting Training...' : 'Start Training'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Upload Dataset Dialog */}
      <Dialog
        open={uploadDatasetDialogOpen}
        onClose={() => {
          setUploadDatasetDialogOpen(false);
          uploadDatasetFormik.resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={uploadDatasetFormik.handleSubmit}>
          <DialogTitle sx={{ fontWeight: 600 }}>Upload Dataset</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              {/* Instance ID Selection */}
              <FormControl 
                fullWidth
                error={uploadDatasetFormik.touched.instance_id && Boolean(uploadDatasetFormik.errors.instance_id)}
              >
                <InputLabel id="upload-instance-select-label">Instance ID</InputLabel>
                <Select
                  labelId="upload-instance-select-label"
                  id="upload-instance-select"
                  name="instance_id"
                  value={uploadDatasetFormik.values.instance_id}
                  label="Instance ID"
                  onChange={uploadDatasetFormik.handleChange}
                  onBlur={uploadDatasetFormik.handleBlur}
                  disabled={loadingInstances}
                >
                  {loadingInstances ? (
                    <MenuItem disabled value="">
                      <CircularProgress size={20} sx={{ mr: 1 }} /> Loading instances...
                    </MenuItem>
                  ) : instances.length === 0 ? (
                    <MenuItem disabled value="">No running instances available</MenuItem>
                  ) : (
                    instances.map((instance) => {
                      const instanceId = String(instance.instance_id);
                      return (
                        <MenuItem key={instanceId} value={instanceId}>
                          {instanceId} - {instance.model_name || instance.name || 'Unnamed Instance'}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
                {uploadDatasetFormik.touched.instance_id && uploadDatasetFormik.errors.instance_id && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.75 }}>
                    {uploadDatasetFormik.errors.instance_id}
                  </Typography>
                )}
              </FormControl>

              {/* File Upload */}
              <input
                id="file-upload"
                type="file"
                accept=".jsonl,.json,.csv"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />
              <label htmlFor="file-upload">
                <Paper
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    p: 6,
                    border: `2px dashed ${
                      uploadDatasetFormik.touched.file && uploadDatasetFormik.errors.file
                        ? '#dc2626'
                        : '#cbd5e1'
                    }`,
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#f8fafc',
                      borderColor: '#94a3b8',
                    }
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: '#64748b', mb: 2 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    {uploadDatasetFormik.values.file
                      ? uploadDatasetFormik.values.file.name
                      : 'Drop file here or click to browse'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Supports JSONL, CSV, JSON formats (max 100MB)
                  </Typography>
                  {uploadDatasetFormik.values.file && (
                    <Typography variant="caption" sx={{ color: '#16a34a', display: 'block', mt: 1 }}>
                      {(uploadDatasetFormik.values.file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  )}
                </Paper>
              </label>
              {uploadDatasetFormik.touched.file && uploadDatasetFormik.errors.file && (
                <Typography variant="caption" sx={{ color: '#dc2626', mt: -2 }}>
                  {uploadDatasetFormik.errors.file}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => {
              setUploadDatasetDialogOpen(false);
              uploadDatasetFormik.resetForm();
            }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              type="submit"
              disabled={!uploadDatasetFormik.values.file || !uploadDatasetFormik.values.instance_id}
            >
              Upload & Validate
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Create Model Confirmation Dialog */}
      <Dialog
        open={createModelDialogOpen}
        onClose={handleCreateModelCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Model Creation</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to create a model configuration from training job <strong>{selectedJob?.name}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
            This will create a new model configuration that can be deployed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCreateModelCancel} disabled={creatingModel}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCreateModelConfirm}
            disabled={creatingModel}
            startIcon={creatingModel ? <CircularProgress size={20} /> : null}
            sx={{ 
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1d4ed8' }
            }}
          >
            {creatingModel ? 'Creating...' : 'Yes, Create Model'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
